import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  ProductExcelExportsFilterParams,
  ProductExcelExportType,
  ProductExcelExportsListResponse,
  CreateProductExcelExportParams
} from '@/types/product-excel-export'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum } from '@/enums'

// API functions
import {
  fetchProductExcelExports,
  fetchProductExcelExport,
  createProductExcelExport,
  deleteProductExcelExport,
  bulkDeleteProductExcelExports
} from '@/lib/api'

type ProductExcelExportStateType = {
  data: ProductExcelExportType | null
  isLoading: boolean
  error: string | null
}

type ProductExcelExportsListStateType = {
  data: ProductExcelExportsListResponse | null
  isLoading: boolean
  error: string | null
}

type ProductExcelExportsStoreState = {
  productExcelExport: ProductExcelExportStateType
  productExcelExportsList: ProductExcelExportsListStateType
  filters: ProductExcelExportsFilterParams
  selectedProductExcelExportIds: string[]
  setFilters: (filters: Partial<ProductExcelExportsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchProductExcelExports: (params?: ProductExcelExportsFilterParams) => Promise<ProductExcelExportsListResponse>
  resetProductExcelExportsList: () => void

  // ProductExcelExport operations
  getProductExcelExport: (id: string) => Promise<ProductExcelExportType>
  createProductExcelExport: (params: CreateProductExcelExportParams) => Promise<ProductExcelExportType>
  deleteProductExcelExport: (id: string) => Promise<void>
  bulkDeleteProductExcelExports: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectProductExcelExport: (id: string) => void
  deselectProductExcelExport: (id: string) => void
  toggleProductExcelExportSelection: (id: string) => void
  selectAllProductExcelExports: (productExcelExports: ProductExcelExportType[]) => void
  deselectAllProductExcelExports: () => void
  isProductExcelExportSelected: (id: string) => boolean
  resetProductExcelExport: () => void
}

const defaultFilters: ProductExcelExportsFilterParams = {
  sort: SortEnum.ASC,
  sortBy: 'translations.en-US.title',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const useProductExcelExportsStore = create<ProductExcelExportsStoreState>()(
  devtools((set, get) => ({
    productExcelExport: {
      data: null,
      isLoading: false,
      error: null
    },
    productExcelExportsList: {
      data: null,
      isLoading: false,
      error: null
    },
    selectedProductExcelExportIds: [],

    filters: defaultFilters,

    setFilters: filters => {
      set(state => ({
        filters: { ...state.filters, ...filters }
      }))
    },

    resetFilters: () => {
      set({ filters: defaultFilters })
    },

    updateUrlParams: searchParams => {
      const filters: Partial<ProductExcelExportsFilterParams> = {}

      for (const [key, value] of searchParams.entries()) {
        if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as ProductExcelExportsFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof ProductExcelExportsFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getProductExcelExport: async id => {
      set(state => ({
        productExcelExport: {
          ...state.productExcelExport,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchProductExcelExport(id)

        set(state => ({
          productExcelExport: {
            ...state.productExcelExport,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productExcelExport: {
            ...state.productExcelExport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createProductExcelExport: async params => {
      set(state => ({
        productExcelExport: {
          ...state.productExcelExport,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createProductExcelExport(params)

        set(state => ({
          productExcelExport: {
            ...state.productExcelExport,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productExcelExport: {
            ...state.productExcelExport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteProductExcelExport: async id => {
      set(state => ({
        productExcelExport: {
          ...state.productExcelExport,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deleteProductExcelExport(id)

        set(state => ({
          productExcelExport: {
            ...state.productExcelExport,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productExcelExport: {
            ...state.productExcelExport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetProductExcelExport: () => {
      set(state => ({
        productExcelExport: {
          ...state.productExcelExport,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectProductExcelExport: id => {
      set(state => {
        if (state.selectedProductExcelExportIds.includes(id)) {
          return state
        }
        return { selectedProductExcelExportIds: [...state.selectedProductExcelExportIds, id] }
      })
    },

    deselectProductExcelExport: id => {
      set(state => ({
        selectedProductExcelExportIds: state.selectedProductExcelExportIds.filter(
          productExcelExportId => productExcelExportId !== id
        )
      }))
    },

    toggleProductExcelExportSelection: id => {
      set(state => {
        const isSelected = state.selectedProductExcelExportIds.includes(id)
        if (isSelected) {
          return {
            selectedProductExcelExportIds: state.selectedProductExcelExportIds.filter(
              productExcelExportId => productExcelExportId !== id
            )
          }
        } else {
          return { selectedProductExcelExportIds: [...state.selectedProductExcelExportIds, id] }
        }
      })
    },

    selectAllProductExcelExports: productExcelExports => {
      set({ selectedProductExcelExportIds: productExcelExports.map(productExcelExport => productExcelExport.id) })
    },

    deselectAllProductExcelExports: () => {
      set({ selectedProductExcelExportIds: [] })
    },

    isProductExcelExportSelected: id => {
      return get().selectedProductExcelExportIds.includes(id)
    },

    bulkDeleteProductExcelExports: async ids => {
      set(state => ({
        productExcelExport: {
          ...state.productExcelExport,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteProductExcelExports({ ids })

        set(state => ({
          productExcelExport: {
            ...state.productExcelExport,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedProductExcelExportIds: state.selectedProductExcelExportIds.filter(
            id => !response.deletedItems.includes(id)
          )
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productExcelExport: {
            ...state.productExcelExport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchProductExcelExports: async (params?: ProductExcelExportsFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        productExcelExportsList: {
          ...state.productExcelExportsList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchProductExcelExports(filtersToUse)

        set(state => ({
          productExcelExportsList: {
            ...state.productExcelExportsList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productExcelExportsList: {
            ...state.productExcelExportsList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetProductExcelExportsList: () => {
      set(state => ({
        productExcelExportsList: {
          ...state.productExcelExportsList,
          data: null,
          error: null
        }
      }))
    }
  }))
)

export default useProductExcelExportsStore
